/** A buffer belongs to its editor; the coordinator never owns document state. */
export type WorkflowEditorBufferState = 'clean' | 'valid' | 'invalid' | 'stale';

/** Adapter for one accurately named pending-input or staged-candidate scope. */
export interface WorkflowEditorPendingScope {
  readonly id: string;
  readonly label: string;
  readonly kind?: 'input' | 'modal-candidate';
  state(): WorkflowEditorBufferState;
  /** A stable value that changes whenever this buffer or its baseline changes. */
  fingerprint(): string | number;
  /** Apply locally, returning false when permission or a current contract refuses. */
  apply?(): boolean;
  /** Reset this buffer only, preserving applied document edits and Undo history. */
  discard(): void;
  focus(): void;
}

/** Bind live child buffers to their owner, including scopes that become pending later. */
export function workflowEditorPendingOwnerScopes(options: {
  readonly id: string;
  readonly owner: () => unknown;
  readonly scopes: () => readonly WorkflowEditorPendingScope[];
  readonly aggregatePending: () => boolean;
  readonly canApply?: () => boolean;
  readonly focus: (scope: WorkflowEditorPendingScope) => void;
}): readonly WorkflowEditorPendingScope[] {
  const owner = options.owner();
  const scopes = options.scopes();
  return [
    {
      id: `${options.id}:context`,
      label: '',
      state: () => 'clean',
      fingerprint: () => {
        const pending = options.scopes().filter(scope => scope.state() !== 'clean');
        return JSON.stringify([
          pending.length ? options.owner() === owner : true,
          options.aggregatePending(),
          pending.map(scope => [scope.id, scope.kind, scope.state(), scope.fingerprint()]),
        ]);
      },
      discard: () => undefined,
      focus: () => undefined,
    },
    ...scopes.map(scope => ({
      id: `${options.id}:${scope.id}`,
      label: scope.label,
      kind: scope.kind,
      state: () => scope.state() === 'clean' ? 'clean' : options.owner() === owner ? scope.state() : 'stale' as const,
      fingerprint: () => scope.state() === 'clean' ? 'clean' : JSON.stringify([options.owner() === owner, scope.fingerprint()]),
      apply: () => options.owner() === owner && options.canApply?.() !== false && (scope.apply?.() ?? false),
      discard: () => { if (options.owner() === owner) scope.discard(); },
      focus: () => options.focus(scope),
    })),
  ];
}

/** Public guard description; the owner supplies the existing shared dialog host. */
export interface WorkflowEditorPendingGuard<TTarget> {
  readonly target: TTarget;
  readonly scopes: readonly {
    readonly id: string;
    readonly label: string;
    readonly state: WorkflowEditorBufferState;
    readonly kind: 'input' | 'modal-candidate';
  }[];
  /** A changed target or buffer requires a fresh navigation request. */
  readonly changed: boolean;
}

/** Only an accepted result authorizes the owner to execute its target. */
export type WorkflowEditorNavigationResult<TTarget> =
  | { readonly accepted: true; readonly target: TTarget }
  | {
      readonly accepted: false;
      readonly guard: WorkflowEditorPendingGuard<TTarget>;
    };

/** Captures only coordination metadata, never working/saved documents or history. */
export class WorkflowEditorPendingState<TTarget> {
  private pending: {
    guard: WorkflowEditorPendingGuard<TTarget>;
    scopes: readonly WorkflowEditorPendingScope[];
    contextScopes: readonly WorkflowEditorPendingScope[];
    states: readonly WorkflowEditorBufferState[];
    fingerprints: readonly (string | number)[];
    context: string | number | undefined;
    contextKey?: () => string | number;
  } | null = null;

  get guard(): WorkflowEditorPendingGuard<TTarget> | null {
    return this.pending?.guard ?? null;
  }

  /** Settle valid input in affected scopes; intentional candidates stay staged. */
  request(
    target: TTarget,
    scopes: readonly WorkflowEditorPendingScope[],
    contextKey?: () => string | number,
  ): WorkflowEditorNavigationResult<TTarget> {
    if (this.pending) return { accepted: false, guard: this.pending.guard };
    for (const scope of scopes) {
      if (scope.state() === 'clean') continue;
      if (
        scope.state() !== 'valid' ||
        scope.kind === 'modal-candidate' ||
        !scope.apply?.() ||
        scope.state() !== 'clean'
      ) {
        const pendingScopes = scopes.filter(
          (entry) => entry.state() !== 'clean',
        );
        const guard: WorkflowEditorPendingGuard<TTarget> = {
          target,
          scopes: pendingScopes.map((entry) => ({
            id: entry.id,
            label: entry.label,
            state: entry.state(),
            kind: entry.kind ?? 'input',
          })),
          changed: false,
        };
        this.pending = {
          guard,
          scopes: pendingScopes,
          contextScopes: scopes,
          states: scopes.map((entry) => entry.state()),
          fingerprints: scopes.map((entry) => entry.fingerprint()),
          context: contextKey?.(),
          contextKey,
        };
        return { accepted: false, guard };
      }
    }
    return { accepted: true, target };
  }

  /** Safe default for Keep editing, Escape, close and backdrop dismissal. */
  keepEditing(): void {
    const scope = this.pending?.scopes[0];
    this.pending = null;
    scope?.focus();
  }

  /** Discard only the captured named buffers after rechecking their exact context. */
  discardAndContinue(): WorkflowEditorNavigationResult<TTarget> | null {
    const pending = this.pending;
    if (!pending) return null;
    if (
      pending.contextKey?.() !== pending.context ||
      pending.contextScopes.some(
        (scope, index) =>
          scope.fingerprint() !== pending.fingerprints[index] ||
          scope.state() !== pending.states[index],
      )
    ) {
      pending.guard = { ...pending.guard, changed: true };
      return { accepted: false, guard: pending.guard };
    }
    for (const scope of pending.scopes) scope.discard();
    // A refused reset must leave the navigation blocked and input recoverable.
    if (pending.scopes.some((scope) => scope.state() !== 'clean')) {
      pending.guard = { ...pending.guard, changed: true };
      return { accepted: false, guard: pending.guard };
    }
    this.pending = null;
    return { accepted: true, target: pending.guard.target };
  }
}
